import { useEffect, useState, useCallback } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { useAppActions, useNav } from '../AppContext'
import { addToast } from '../services/Toasts'
import { fetchSmInit } from '../services/TemplatesService'
import TabHeader from '../comps/TabHeader'
import ManageStylesTab from './templates/ManageStylesTab'
import ManageTweaksTab from './templates/ManageTweaksTab'
import ManageScoringTab from './templates/ManageScoringTab'
import ManageWinThemesTab from './templates/ManageWinThemesTab'
import type { TemplatesSubTab } from '../types'

const SUB_TABS = [
  { key: 'style' as TemplatesSubTab, label: 'Style' },
  { key: 'tweaks' as TemplatesSubTab, label: 'Tweaks' },
  { key: 'scoring' as TemplatesSubTab, label: 'Scoring' },
  { key: 'winthemes' as TemplatesSubTab, label: 'Win Themes' },
]

const TEMPLATE_SUB_KEYS = SUB_TABS.map(s => s.key)

function templatesSubTabFromUrl(): TemplatesSubTab {
  const parts = window.location.pathname.split('/').filter(Boolean)
  // ['app', 'templates', subtab?]
  const sub = parts[2] as TemplatesSubTab
  return TEMPLATE_SUB_KEYS.includes(sub) ? sub : 'style'
}

interface SmInit {
  stylegroups?: any[]
  elementgroups?: any[]
  scoringtemplates?: any[]
  winthemetemplates?: any[]
}

export default function TemplatesTab() {
  const { setStyleNames, setElementGroups } = useAppActions()
  const { activeTab } = useNav()
  const [viewSelected, setViewSelected] = useState<TemplatesSubTab>(() => templatesSubTabFromUrl())
  const [smInit, setSmInit] = useState<SmInit>({})
  const [loading, setLoading] = useState(false)

  // Mirror the active sub-tab into the URL.
  useEffect(() => {
    if (activeTab !== 'styletab') return
    const target = `/app/templates/${viewSelected}`
    if (window.location.pathname !== target) {
      window.history.pushState({}, '', target)
    }
  }, [viewSelected, activeTab])

  // Re-derive on back/forward.
  useEffect(() => {
    const onPop = () => setViewSelected(templatesSubTabFromUrl())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const handleRefresh = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchSmInit() as SmInit | null
      if (data) {
        setSmInit(data)
        // Keep AppContext in sync - the tweak picker modal reads
        // `elementGroups` from there.
        if (data.stylegroups) setStyleNames((data.stylegroups as any[]).map(g => g.name as string))
        if (data.elementgroups) setElementGroups(data.elementgroups as any[])
      }
    } catch {
      addToast('error', 'Failed to load templates.')
    } finally {
      setLoading(false)
    }
  }, [addToast, setStyleNames, setElementGroups])

  useEffect(() => { handleRefresh() }, [handleRefresh])

  const styleTemplates = smInit.stylegroups ?? []
  const elementGroups = smInit.elementgroups ?? []
  const scoringTemplates = smInit.scoringtemplates ?? []
  const winThemes = smInit.winthemetemplates ?? []

  return (
    <Box data-testid="templates_root" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      <TabHeader
        tabs={SUB_TABS}
        active={viewSelected}
        onChange={setViewSelected}
        endSlot={loading ? <CircularProgress size={14} /> : null}
        testIdPrefix="templates"
      />

      {/* Sub-tab content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {viewSelected === 'style' && (
          <ManageStylesTab styleTemplates={styleTemplates} onRefresh={handleRefresh} />
        )}
        {viewSelected === 'tweaks' && (
          <ManageTweaksTab groups={elementGroups} onRefresh={handleRefresh} />
        )}
        {viewSelected === 'scoring' && (
          <ManageScoringTab templates={scoringTemplates} onRefresh={handleRefresh} />
        )}
        {viewSelected === 'winthemes' && (
          <ManageWinThemesTab winthemes={winThemes} onRefresh={handleRefresh} />
        )}
      </Box>
    </Box>
  )
}
